import { ITransaction } from '@/api/commitment';
import { AddressType } from '@/types/address';
import { TransactionType, TransactionTypeName } from '@/types/commitment';
import dayjs from 'dayjs';
import { Hash } from 'viem';

/**
 * 获取交易金额
 * 如果交易有inputs,则是input的amount总和
 * 如果交易没有inputs,则是outputs的amount总和
 * @param transaction 交易
 * @returns 交易金额
 */
export function getTransactionAmount(transaction: ITransaction) {
  if (transaction.inputs && transaction.inputs.length > 0) {
    return transaction.inputs.reduce((acc, input) => acc + BigInt(input.amount), BigInt(0));
  }

  if (transaction.tx_type === TransactionType.Deposit && transaction.outputs && transaction.outputs.length > 0) {
    return transaction.outputs.reduce((acc, output) => acc + BigInt(output.amount), BigInt(0));
  }

  return BigInt(0);
}

/**
 * 获取交易的From地址
 * 如果有inputs，返回inputs的user_pk
 * 如果没有inputs但有outputs且有from_address，返回evm的from_address
 * @param transaction 交易
 * @param deduplication 是否去重
 * @returns From地址数组
 */
export function getTransactionFromAddresses(transaction: ITransaction, deduplication: boolean = true) {
  const revert: Array<{ address: Hash; type: AddressType }> = [];
  if (transaction.inputs && transaction.inputs.length > 0) {
    const addresses = transaction.inputs.map((input) => input.user_pk);
    const addressList = deduplication ? [...new Set(addresses)] : addresses;
    addressList.forEach((address) => {
      revert.push({ address, type: AddressType.APP });
    });
  }
  if (transaction.tx_type === TransactionType.Deposit && transaction.evm_address) {
    revert.push({ address: transaction.evm_address, type: AddressType.EVM });
  }
  return revert;
}

/**
 * 获取交易的To地址
 * 如果有outputs，返回outputs的user_pk
 * @param transaction 交易
 * @param deduplication 是否去重
 * @returns To地址数组
 */
export function getTransactionToAddresses(transaction: ITransaction, deduplication: boolean = true) {
  const revert: Array<{ address: Hash; type: AddressType }> = [];
  if (transaction.outputs && transaction.outputs.length > 0) {
    const addresses = transaction.outputs.map((output) => output.user_pk);
    const addressList = deduplication ? [...new Set(addresses)] : addresses;
    addressList.forEach((address) => {
      revert.push({ address, type: AddressType.APP });
    });
  }
  if (transaction.tx_type === TransactionType.Withdraw && transaction.evm_address) {
    revert.push({ address: transaction.evm_address, type: AddressType.EVM });
  }
  return revert;
}

/**
 * 获取交易类型名称
 * @param txType 交易类型
 * @returns 交易类型名称
 */
export function getTransactionTypeName(txType: TransactionType): string {
  return TransactionTypeName[txType] || 'Unknown';
}

export function getTransactionAssetId(transaction: ITransaction): Hash | undefined {
  if (transaction.inputs && transaction.inputs.length > 0) {
    return transaction.inputs[0].asset;
  }
  if (transaction.outputs && transaction.outputs.length > 0) {
    return transaction.outputs[0].asset;
  }
  return undefined;
}

/**
 * 格式化时间戳
 * @param timestamp 时间戳（秒或毫秒）
 * @returns 格式化后的时间字符串
 */
export function formatTransactionTime(timestamp: string | number): string {
  const time = typeof timestamp === 'string' ? parseInt(timestamp) : timestamp;
  // 如果是秒级时间戳，转换为毫秒
  const ms = time < 10000000000 ? time * 1000 : time;
  return dayjs(ms).format('YYYY/MM/DD HH:mm:ss');
}

const splitTx = '-';

export function parseTransactionHash(tx: string): [string, number] {
  const [txhash, tx_index] = tx.split(splitTx);
  if (!txhash || !tx_index) {
    // 如果txhash或tx_index为空，则返回空字符串和-1
    return ['', -1];
  }
  return [txhash, parseInt(tx_index)];
}

export function serializeTransactionHash(txhash: string, tx_index: number): string {
  if (txhash === '' || tx_index === -1) {
    return '';
  }
  return txhash + splitTx + tx_index.toString();
}
